import type { UserPermissionsByDatabaseDto } from '@/libs/api-user-mgt/generated/model';
import { userManagementAxiosInstance } from '@/libs/api-user-mgt';

import { mapCrossUserPayloadToWireDto } from '@/userManagement/application/mappers/crossUserListItemMapper';
import { mapUserDtoToEntity } from '@/userManagement/application/mappers/userMapper';
import type { UserListParams } from '@/userManagement/domain/types/QueryContracts';
import type { Feature } from '@/userManagement/domain/entities/Feature';
import type { User } from '@/userManagement/domain/entities/User';

import { normalizeFeaturesResponse } from './featuresWire';
import { toUserManagementUsersQueryParams } from './sieveQuery';
import { coerceAxiosSnapshotFileDataToArrayBuffer, snapshotFileBytesToUtf8JsonText } from './snapshotPermissionFile';
import { normalizeUserPermissionsByDatabaseDto } from './userPermissionsFetch';

const DIRECTORY_BUNDLE_PATH = '/api/user-management/v1/users/directory-bundle';

/** One row after normalizing `GET .../users/directory-bundle` JSON. */
export interface UserDirectoryBundleEntry {
  crossUserId: string;
  username: string;
  displayName: string;
  permissionRoleId: number | null;
  permissionsDto: UserPermissionsByDatabaseDto | undefined;
  /** Full user row for {@link finalizeUserPermissionsFromCached} (same shape as per-user GET). */
  user: User;
}

export interface UserDirectoryBundleParsed {
  features: Feature[];
  entries: UserDirectoryBundleEntry[];
}

function firstUsersArray(root: Record<string, unknown>): unknown[] | undefined {
  const users = root.users ?? root.Users;
  return Array.isArray(users) ? users : undefined;
}

function parseDirectoryBundleJson(parsed: unknown): UserDirectoryBundleParsed {
  if (parsed === null || typeof parsed !== 'object') {
    return { features: [], entries: [] };
  }
  const root = parsed as Record<string, unknown>;
  const featuresRaw = root.features ?? root.Features;
  const features = normalizeFeaturesResponse(featuresRaw);
  const usersArr = firstUsersArray(root) ?? [];

  const entries: UserDirectoryBundleEntry[] = [];
  for (const raw of usersArr) {
    if (raw === null || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    const profile = row.profile ?? row.Profile;
    const permissionsRaw = row.permissions ?? row.Permissions;
    const wire = mapCrossUserPayloadToWireDto(profile);
    const user = wire != null ? mapUserDtoToEntity(wire) : null;
    const crossUserId = user?.id?.trim() ?? '';
    const username = user?.username?.trim() ?? '';
    if (!user || !crossUserId || !username) continue;
    const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || username;
    const permissionsDto = normalizeUserPermissionsByDatabaseDto(permissionsRaw);
    entries.push({
      crossUserId,
      username,
      displayName,
      permissionRoleId: user.permissionRoleId ?? null,
      permissionsDto,
      user
    });
  }
  return { features, entries };
}

export type DirectoryBundleListParams = Pick<
  UserListParams,
  'search' | 'filters' | 'sorts' | 'roleIds' | 'databases' | 'hasRoleDeviation' | 'changesOnly'
>;

export interface FetchUserDirectoryBundleOptions {
  /** Sieve-style filters (server ignores pagination for this route). */
  listParams?: DirectoryBundleListParams;
  /**
   * When true (default), response is gzip; FE decompresses then parses JSON.
   * When false, response is plain JSON (easier debugging).
   */
  compressed?: boolean;
}

/**
 * `GET /api/user-management/v1/users/directory-bundle` — features plus all matching users with profile + permissions
 * in one payload (avoids per-user permissions calls).
 */
export async function fetchUserDirectoryBundle(options?: FetchUserDirectoryBundleOptions): Promise<UserDirectoryBundleParsed> {
  const compressed = options?.compressed !== false;
  const listParams = options?.listParams ?? {};
  const sieve = toUserManagementUsersQueryParams({
    page: 1,
    pageSize: 1,
    ...listParams
  });

  const params: Record<string, unknown> = {
    compressed,
    Filters: sieve.Filters,
    Sorts: sieve.Sorts
  };

  if (compressed) {
    const res = await userManagementAxiosInstance<ArrayBuffer>({
      url: DIRECTORY_BUNDLE_PATH,
      method: 'GET',
      params,
      responseType: 'arraybuffer'
    });
    const buf = coerceAxiosSnapshotFileDataToArrayBuffer(res.data);
    const text = await snapshotFileBytesToUtf8JsonText(buf);
    const parsed = JSON.parse(text) as unknown;
    return parseDirectoryBundleJson(parsed);
  }

  const res = await userManagementAxiosInstance<unknown>({
    url: DIRECTORY_BUNDLE_PATH,
    method: 'GET',
    params: { ...params, compressed: false }
  });
  return parseDirectoryBundleJson(res.data);
}
